import {
  ArrowRight,
  Bookmark,
  BookOpen,
  CalendarDays,
  CheckCircle,
  Clock,
  Loader2,
  ReceiptText,
  ShoppingCart,
  Trash2,
  X,
} from 'lucide-react';
import React from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { FormatDate } from '../../../components/FormatDate';
import { EducationTemplate } from '../../../domain/Domain';
import { setSelectedProduct } from '../../../api/reducers/selectedProductReducer';
import { educationAlpha, useActiveEducationTheme } from '../../../template/EducationThemeBridge.jsx';

const EducationCart = ({
  items = [],
  totalAmount = 0,
  currencySymbol = '',
  onRemove,
  onRemoveItem,
  onDeleteCart,
  onCheckout,
  onContinueShopping,
  onEmptyCart,
  userDetail,
  isLoading,
  isProcessing
}) => {
  const educationTheme = useActiveEducationTheme();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [openDeleteDialog, setOpenDeleteDialog] = React.useState(false);

  const cartItems = React.useMemo(() => (Array.isArray(items) ? items : []), [items]);
  const totalItems = cartItems.length;
  const removeCartItem = onRemove || onRemoveItem || (() => {});
  const emptyCart = onEmptyCart || onDeleteCart || (() => {});
  const continueShopping = onContinueShopping || (() => navigate('/products'));
  const cartTotal = Number(totalAmount || 0);

  const colors = React.useMemo(() => ({
    primary: educationTheme.primary,
    secondary: educationTheme.secondary,
    accent: '#B5A262',
    background: educationTheme.background,
    paper: educationTheme.surface,
    textDark: educationTheme.text,
    textLight: educationTheme.muted,
    border: educationTheme.border,
    success: '#00b894',
    warning: '#fdcb6e',
    error: '#d63031',
    info: educationTheme.secondary,
    primarySoft: educationAlpha(educationTheme.primary, 0.08),
    primaryTint: educationAlpha(educationTheme.primary, 0.1),
    primaryHover: educationAlpha(educationTheme.primary, 0.15),
    primaryBorder: educationAlpha(educationTheme.primary, 0.2),
    primaryShadow: educationAlpha(educationTheme.primary, 0.3),
    primaryStrongShadow: educationAlpha(educationTheme.primary, 0.4),
    secondarySoft: educationAlpha(educationTheme.secondary, 0.08),
    infoSoft: educationAlpha(educationTheme.secondary, 0.08),
    mutedDisabled: educationAlpha(educationTheme.muted, 0.6),
  }), [educationTheme]);

  const groupedItems = React.useMemo(() => {
    return cartItems.reduce((groups, item) => {
      const hasStartOrVirtual = item.scheduledProduct?.period?.effectiveStartDate ||
        item.product?.deliveryType === "VIRTUAL";
      const startDate = hasStartOrVirtual ? "VIRTUAL" : "NO_DATE";

      if (!groups[startDate]) {
        groups[startDate] = [];
      }
      groups[startDate].push(item);
      return groups;
    }, {});
  }, [cartItems]);

  const getDeliveryTypeLabel = (deliveryType, deliverySubType) => {
    if (deliveryType === "VIRTUAL") {
      if (deliverySubType === "INSTRUCTOR_LED") {
        return {
          label: "Instructor-led Online",
          description: "Live virtual sessions with instructor"
        };
      }

      return {
        label: "Self-paced Online",
        description: "Learn at your own pace"
      };
    }

    if (deliveryType === "BRICK_MORTAR") {
      return {
        label: "In-Classroom Experience",
        description: "Recommended experience"
      };
    }

    if (deliveryType === "DIGITAL") {
      return {
        label: "Online",
        description: "Join online sessions"
      };
    }

    return {
      label: deliveryType || "Delivery Type",
      description: "Recommended experience"
    };
  };

  const getInstructorName = (item, product) => {
    const owner = item.scheduledProduct?.owners?.[0];

    if (owner?.firstName || owner?.lastName) {
      return `${owner.firstName || ''} ${owner.lastName || ''}`.trim();
    }

    const contactPerson = product?.company?.contactPerson;
    if (contactPerson?.firstName || contactPerson?.lastName) {
      return `${contactPerson.firstName || ''} ${contactPerson.lastName || ''}`.trim();
    }

    return 'Not specified';
  };

  const getCourseRoute = (item) => {
    return item.scheduledProduct?.product || item.product ? '/course-detail' : null;
  };

  const getTagStyles = (isVirtual, deliverySubType, variant = 'delivery') => {
    if (variant === 'duration') {
      return {
        color: colors.primary,
        borderColor: colors.primary,
        backgroundColor: colors.primarySoft,
      };
    }

    if (variant === 'date') {
      return {
        color: colors.secondary,
        borderColor: colors.secondary,
        backgroundColor: colors.secondarySoft,
      };
    }

    if (isVirtual) {
      const accentColor = deliverySubType === "INSTRUCTOR_LED" ? colors.secondary : colors.info;
      return {
        color: accentColor,
        borderColor: accentColor,
        backgroundColor: deliverySubType === "INSTRUCTOR_LED" ? colors.secondarySoft : colors.infoSoft,
      };
    }

    return {
      color: colors.primary,
      borderColor: colors.primary,
      backgroundColor: colors.primarySoft,
    };
  };

  const CourseTag = ({ icon: Icon, label, style }) => (
    <span
      className="inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs font-medium"
      style={style}
    >
      {Icon && <Icon size={14} />}
      {label}
    </span>
  );

  const renderCourseCard = (item, index) => {
    const product = item.scheduledProduct?.product || item.product;
    const schedule = item.scheduledProduct?.period;
    const isVirtual = product?.deliveryType === "VIRTUAL";
    const deliverySubType = product?.deliverySubType;
    const deliveryLabel = getDeliveryTypeLabel(product?.deliveryType, deliverySubType);
    const productId = item.scheduledProduct?.primaryId || item.product?.primaryId;
    const courseRoute = getCourseRoute(item);

    let durationInWeeks = 0;
    if (schedule?.effectiveStartDate && schedule?.effectiveEndDate) {
      durationInWeeks = Math.round(
        (new Date(schedule.effectiveEndDate) - new Date(schedule.effectiveStartDate)) /
        (1000 * 60 * 60 * 24 * 7)
      );
    }

    const handleCardClick = () => {
      if (courseRoute) {
        dispatch(setSelectedProduct(product));
        navigate(courseRoute);
      }
    };

    const handleCardKeyDown = (event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        handleCardClick();
      }
    };

    return (
      <article
        id={`course-card-${productId || index}`}
        role="button"
        tabIndex={0}
        onClick={handleCardClick}
        onKeyDown={handleCardKeyDown}
        className="group relative mb-3 flex cursor-pointer flex-col gap-6 overflow-hidden rounded-3xl p-6 transition duration-300 sm:flex-row"
        style={{
          backgroundColor: colors.paper,
          border: `1px solid ${colors.border}`,
        }}
        onMouseEnter={(event) => {
          event.currentTarget.style.boxShadow = `0 8px 24px ${colors.primaryHover}`;
          event.currentTarget.style.transform = 'translateY(-2px)';
          event.currentTarget.style.borderColor = colors.primary;
        }}
        onMouseLeave={(event) => {
          event.currentTarget.style.boxShadow = 'none';
          event.currentTarget.style.transform = 'translateY(0)';
          event.currentTarget.style.borderColor = colors.border;
        }}
      >
        <div
          id="course-card-image"
          className="relative flex h-40 w-full shrink-0 items-center justify-center overflow-hidden rounded-2xl sm:h-[120px] sm:w-[200px]"
          style={{
            backgroundColor: colors.background,
            border: `1px solid ${colors.border}`,
          }}
        >
          {product?.imageContents?.length > 0 ? (
            <img
              id="course-image"
              src={
                product.imageContents[0].displayLink ||
                product.imageContents[0].storedLink
              }
              alt={product?.name}
              className="h-full w-full object-cover transition duration-300 group-hover:scale-105"
            />
          ) : (
            <BookOpen
              id="default-course-icon"
              size={48}
              color={colors.textLight}
              opacity={0.3}
            />
          )}
        </div>

        <div className="flex min-w-0 flex-1 flex-col justify-between">
          <div id="course-card-content">
            <div className="mb-1 flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
              <h2
                id="course-name"
                className="pr-2 text-lg font-bold leading-snug"
                style={{ color: colors.textDark }}
              >
                {product?.name}
              </h2>

              <div
                id="course-price"
                className="min-w-[70px] self-start rounded-2xl px-4 py-2 text-right text-lg font-extrabold"
                style={{
                  color: colors.primary,
                  backgroundColor: colors.primarySoft,
                  border: `1px solid ${colors.primaryBorder}`,
                }}
              >
                {currencySymbol}
                {Number(product?.price?.priceAmt || 0).toFixed(0)}
              </div>
            </div>

            {!isVirtual && (
              <div
                id="course-instructor"
                className="mb-4 flex items-center gap-2 text-sm font-medium"
                style={{ color: colors.secondary }}
              >
                <Bookmark size={16} color={colors.accent} />
                <span>Instructor: {getInstructorName(item, product)}</span>
              </div>
            )}

            <div id="course-tags" className="mb-4 flex flex-wrap gap-2">
              {deliveryLabel && (
                <CourseTag
                  label={deliveryLabel.label}
                  style={getTagStyles(isVirtual, deliverySubType)}
                />
              )}

              {!isVirtual && durationInWeeks > 0 && (
                <CourseTag
                  icon={Clock}
                  label={`${durationInWeeks} week${durationInWeeks > 1 ? 's' : ''}`}
                  style={getTagStyles(isVirtual, deliverySubType, 'duration')}
                />
              )}

              {!isVirtual && schedule?.effectiveStartDate && schedule?.effectiveEndDate && (
                <CourseTag
                  icon={CalendarDays}
                  label={`${FormatDate(
                    schedule.effectiveStartDate,
                    'MMM d'
                  )} - ${FormatDate(schedule.effectiveEndDate, 'MMM d')}`}
                  style={getTagStyles(isVirtual, deliverySubType, 'date')}
                />
              )}
            </div>
          </div>
        </div>

        <div className="flex justify-end self-start sm:ml-auto">
          <button
            type="button"
            id={`remove-course-button-${productId || index}`}
            className="inline-flex items-center gap-1.5 rounded-md border px-4 py-2 text-sm font-semibold transition"
            onClick={(event) => {
              event.stopPropagation();
              removeCartItem(productId);
            }}
            style={{
              color: colors.error,
              borderColor: colors.error,
              backgroundColor: 'transparent',
            }}
            onMouseEnter={(event) => {
              event.currentTarget.style.backgroundColor = `${colors.error}08`;
            }}
            onMouseLeave={(event) => {
              event.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            <X size={16} />
            Remove
          </button>
        </div>
      </article>
    );
  };

  return (
    <EducationTemplate>
      <div className="h-10" aria-hidden="true" />
      <section
        className="min-h-screen py-12"
        style={{ backgroundColor: colors.background }}
      >
        <div className="mx-auto max-w-[1280px] px-4">
          <div className="mb-10 flex flex-col items-center justify-between gap-4 text-center sm:flex-row sm:text-left">
            <div>
              <h1
                className="flex flex-wrap items-center justify-center gap-4 text-[1.8rem] font-extrabold sm:justify-start sm:text-[2.2rem]"
                style={{ color: colors.textDark }}
              >
                <span className="relative inline-flex">
                  <ShoppingCart size={42} color={colors.primary} />
                  {totalItems > 0 && (
                    <span
                      className="absolute -right-2 -top-2 inline-flex min-h-[26px] min-w-[26px] items-center justify-center rounded-full px-2 text-xs font-bold"
                      style={{
                        backgroundColor: colors.primary,
                        color: colors.paper,
                      }}
                    >
                      {totalItems}
                    </span>
                  )}
                </span>
                {userDetail?.firstName || 'Your'}'s Learning Cart
              </h1>
            </div>

            {totalItems > 0 && (
              <button
                type="button"
                className="inline-flex w-full items-center justify-center gap-2 rounded-2xl border px-5 py-3 font-semibold transition sm:w-auto"
                onClick={() => setOpenDeleteDialog(true)}
                style={{
                  color: colors.error,
                  borderColor: colors.error,
                  backgroundColor: 'transparent',
                }}
                onMouseEnter={(event) => {
                  event.currentTarget.style.backgroundColor = `${colors.error}08`;
                }}
                onMouseLeave={(event) => {
                  event.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                <Trash2 size={18} />
                Delete All
              </button>
            )}
          </div>

          {isLoading ? (
            <div
              className="rounded-3xl border py-16 text-center shadow-sm"
              style={{
                backgroundColor: colors.paper,
                borderColor: colors.border,
              }}
            >
              <h2 className="mb-6 text-xl font-bold" style={{ color: colors.textDark }}>
                Loading your learning journey...
              </h2>
              <div
                className="mx-auto h-1.5 w-3/5 overflow-hidden rounded-full"
                style={{ backgroundColor: colors.background }}
              >
                <div
                  className="h-full w-3/5 animate-pulse rounded-full"
                  style={{
                    background: `linear-gradient(90deg, ${colors.primary} 0%, ${colors.secondary} 100%)`,
                  }}
                />
              </div>
            </div>
          ) : totalItems > 0 ? (
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
              <div className="lg:col-span-2">
                <div
                  className="overflow-hidden rounded-3xl border shadow-sm"
                  style={{
                    backgroundColor: colors.paper,
                    borderColor: colors.border,
                  }}
                >
                  {Object.entries(groupedItems).map(([startDate, dateItems]) => (
                    <div key={startDate}>
                      {dateItems.map((item, index) => (
                        <div key={`${startDate}-${index}`} className="px-4 py-3">
                          {renderCourseCard(item, index)}
                          {index < dateItems.length - 1 && (
                            <div
                              className="my-4 border-t border-dashed"
                              style={{ borderColor: colors.border }}
                            />
                          )}
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              </div>

              <aside className="lg:col-span-1">
                <div
                  className="overflow-hidden rounded-3xl border shadow-sm lg:sticky lg:top-5"
                  style={{
                    backgroundColor: colors.paper,
                    borderColor: colors.border,
                  }}
                >
                  <div
                    className="border-b p-6"
                    style={{
                      backgroundColor: colors.primary,
                      borderColor: colors.border,
                      color: colors.paper,
                    }}
                  >
                    <h2 className="mb-1 flex items-center gap-2 text-2xl font-bold">
                      <ReceiptText size={26} />
                      Order Summary
                    </h2>
                    <p className="text-sm opacity-90">
                      Review your selected courses before enrollment
                    </p>
                  </div>

                  <div className="p-6">
                    <div className="mb-4">
                      {cartItems.slice(0, 3).map((item, index) => {
                        const product = item.scheduledProduct?.product || item.product;
                        return (
                          <div key={index} className="mb-3 flex items-center justify-between gap-3">
                            <span
                              className="min-w-0 flex-1 truncate text-sm font-medium"
                              style={{ color: colors.textDark }}
                            >
                              {product?.name}
                            </span>
                            <span
                              className="text-sm font-bold"
                              style={{ color: colors.primary }}
                            >
                              {currencySymbol}
                              {Number(product?.price?.priceAmt || 0).toFixed(2)}
                            </span>
                          </div>
                        );
                      })}
                      {cartItems.length > 3 && (
                        <p
                          className="mt-2 text-center text-sm italic"
                          style={{ color: colors.textLight }}
                        >
                          +{cartItems.length - 3} more course{cartItems.length - 3 !== 1 ? 's' : ''}
                        </p>
                      )}
                    </div>

                    <div className="my-4 border-t border-dashed" style={{ borderColor: colors.border }} />

                    <div className="mb-4 space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm" style={{ color: colors.textLight }}>Subtotal</span>
                        <span className="text-sm font-semibold" style={{ color: colors.textDark }}>
                          {currencySymbol}{cartTotal.toFixed(2)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm" style={{ color: colors.textLight }}>Taxes</span>
                        <span className="text-sm font-semibold" style={{ color: colors.textDark }}>
                          {currencySymbol}0.00
                        </span>
                      </div>
                    </div>

                    <div className="my-4 border-t-2" style={{ borderColor: colors.border }} />

                    <div className="mb-6 flex items-center justify-between">
                      <span className="text-lg font-extrabold" style={{ color: colors.textDark }}>
                        Total Amount
                      </span>
                      <span className="text-xl font-extrabold" style={{ color: colors.primary }}>
                        {currencySymbol}{cartTotal.toFixed(2)}
                      </span>
                    </div>

                    <button
                      type="button"
                      id="checkout-button"
                      className="mb-3 inline-flex w-full items-center justify-center gap-2 rounded-2xl px-5 py-4 text-base font-bold text-white shadow-md transition disabled:cursor-not-allowed"
                      onClick={onCheckout}
                      disabled={isProcessing}
                      style={{
                        background: isProcessing
                          ? colors.mutedDisabled
                          : `linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 100%)`,
                        color: colors.paper,
                        boxShadow: isProcessing ? 'none' : `0 4px 12px ${colors.primaryShadow}`,
                      }}
                    >
                      {isProcessing ? (
                        <>
                          <Loader2 size={18} className="animate-spin" />
                          Processing payment...
                        </>
                      ) : (
                        <>
                          <CheckCircle size={18} />
                          Proceed to payment
                        </>
                      )}
                    </button>

                    <button
                      type="button"
                      className="inline-flex w-full items-center justify-center gap-2 rounded-2xl border px-5 py-4 text-base font-semibold transition"
                      onClick={continueShopping}
                      style={{
                        borderColor: colors.secondary,
                        color: colors.secondary,
                        backgroundColor: 'transparent',
                      }}
                      onMouseEnter={(event) => {
                        event.currentTarget.style.backgroundColor = colors.secondarySoft;
                      }}
                      onMouseLeave={(event) => {
                        event.currentTarget.style.backgroundColor = 'transparent';
                      }}
                    >
                      Explore More Courses
                      <ArrowRight size={18} />
                    </button>

                    <p
                      className="mt-6 text-center text-xs italic"
                      style={{ color: colors.textLight }}
                    >
                      Need help? Contact our academic advisors for assistance.
                    </p>
                  </div>
                </div>
              </aside>
            </div>
          ) : (
            <div
              className="relative mx-auto mt-4 max-w-[700px] overflow-hidden rounded-3xl border p-10 text-center shadow-md sm:p-14"
              style={{
                backgroundColor: colors.paper,
                borderColor: colors.border,
              }}
            >
              <div
                className="absolute left-0 right-0 top-0 h-1.5"
                style={{
                  background: `linear-gradient(90deg, ${colors.primary} 0%, ${colors.secondary} 100%)`
                }}
              />
              <div
                className="mx-auto mb-6 flex h-[120px] w-[120px] items-center justify-center rounded-full"
                style={{ backgroundColor: colors.primaryTint }}
              >
                <ShoppingCart size={60} color={colors.primary} opacity={0.8} />
              </div>
              <h2
                className="mb-4 text-[1.8rem] font-extrabold sm:text-[2.2rem]"
                style={{ color: colors.textDark }}
              >
                Your Learning Cart is Empty
              </h2>
              <p
                className="mx-auto mb-8 max-w-[500px] text-lg leading-relaxed"
                style={{ color: colors.textLight }}
              >
                Discover world-class courses and start your educational journey today.
                Browse our catalog to find the perfect program for your goals.
              </p>
              <a
                href="/products"
                className="inline-flex items-center justify-center gap-2 rounded-2xl px-8 py-4 text-base font-bold text-white shadow-md transition"
                style={{
                  background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 100%)`,
                  boxShadow: `0 4px 12px ${colors.primaryShadow}`,
                }}
              >
                Browse All Courses
                <ArrowRight size={18} />
              </a>
            </div>
          )}
        </div>
      </section>

      {openDeleteDialog && (
        <div
          className="fixed inset-0 z-[200] flex items-center justify-center px-4"
          role="presentation"
        >
          <button
            type="button"
            className="absolute inset-0 h-full w-full bg-black/40"
            onClick={() => setOpenDeleteDialog(false)}
            aria-label="Close delete confirmation"
          />
          <div
            className="relative w-full max-w-[500px] overflow-hidden rounded-3xl shadow-2xl"
            role="dialog"
            aria-modal="true"
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
            style={{ backgroundColor: colors.paper }}
          >
            <div
              id="alert-dialog-title"
              className="border-b px-6 py-4 text-xl font-bold"
              style={{
                color: colors.textDark,
                backgroundColor: colors.background,
                borderColor: colors.border,
              }}
            >
              Confirm Cart Deletion
            </div>
            <div className="px-6 py-6">
              <div className="mb-4 flex justify-center">
                <div
                  className="flex h-[60px] w-[60px] items-center justify-center rounded-full"
                  style={{ backgroundColor: `${colors.error}10` }}
                >
                  <Trash2 size={32} color={colors.error} />
                </div>
              </div>
              <p
                id="alert-dialog-description"
                className="text-center text-base leading-relaxed"
                style={{ color: colors.textDark }}
              >
                Are you sure you want to permanently delete all items from your cart?
              </p>
              <p
                className="mt-2 text-center text-sm"
                style={{ color: colors.textLight }}
              >
                This action cannot be undone and you'll need to add courses again.
              </p>
            </div>
            <div
              className="flex justify-end gap-3 border-t px-6 py-4"
              style={{ borderColor: colors.border }}
            >
              <button
                type="button"
                className="rounded-2xl border px-5 py-2.5 font-semibold transition"
                onClick={() => setOpenDeleteDialog(false)}
                style={{
                  borderColor: colors.border,
                  color: colors.textDark,
                  backgroundColor: 'transparent',
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="rounded-2xl px-5 py-2.5 font-semibold text-white transition"
                autoFocus
                onClick={() => {
                  emptyCart();
                  setOpenDeleteDialog(false);
                }}
                style={{ backgroundColor: colors.error }}
              >
                Delete All Items
              </button>
            </div>
          </div>
        </div>
      )}
    </EducationTemplate>
  );
};

export default EducationCart;
